import React from "react";
import Head from "next/head";
import compact from "lodash/compact";
import styled from "styled-components";
import Layout from "components/Layout";
import { Container } from "components/Grid";
import Hero from "components/category/Hero";
import QuizTile from "components/QuizTile";
import { getBySlug, getCategoryBySlug } from "lib/models/Navigation";
import { getAll as getAllQuizzes } from "lib/models/Quiz";
import withUser from "lib/user/withUser";
import withGlobalFields from "lib/withGlobalFields";

const Intro = styled.p`
  font-size: 2rem;
  text-align: center;
`;

const Sections = styled.div`
  padding-top: 2rem;
`;

const Section = styled.div`
  margin-bottom: 3rem;
`;

const Title = styled.h2`
  display: block;
  padding: 1rem 0;
  margin-bottom: 2rem;
  font-size: 2.6rem;
  border-bottom: 2px solid #666;
`;

const List = styled.ul`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -1.5rem;
`;

const ListItem = styled.li`
  flex: 0 1 100%;
  padding: 0 1.5rem;

  @media (min-width: 640px) {
    flex: 0 1 50%;
  }

  @media (min-width: 992px) {
    flex: 0 1 33.33%;
  }
`;

const SubCategory = (props) => {
  return (
    <Layout {...props.global}>
      <Head>
        <title>{`${props.title} | ${props.parent} | ${props.global.seo.title}`}</title>
      </Head>

      {props.mainQuiz && <Hero {...props.mainQuiz} />}

      <Container>
        {props.mainQuiz && <Intro>{props.text}</Intro>}

        <Sections>
          {props.sections.map((section) => {
            if (!section.quizzes.length) return null;

            return (
              <Section key={section.id}>
                <Title>{section.title}</Title>

                <List>
                  {section.quizzes.map((quiz) => (
                    <ListItem key={quiz.id}>
                      <QuizTile {...quiz} />
                    </ListItem>
                  ))}
                </List>
              </Section>
            );
          })}
        </Sections>
      </Container>
    </Layout>
  );
};

export const getServerSideProps = withGlobalFields(
  withUser(async ({ params }, user) => {
    const category = await getBySlug(params.category);
    if (!category) return { notFound: true };

    let subCategory = await getCategoryBySlug(category.id, params.subCategory);
    if (!subCategory) return { notFound: true };

    let { title, text, mainQuiz, sections } = subCategory;

    const quizzes = await getAllQuizzes(
      ["name", "slug", "image"],
      user.country
    );

    // Attach mainQuiz if exists
    if (mainQuiz) mainQuiz = quizzes.find((q) => q.id === mainQuiz);

    // Attach Quizzes to sections
    sections = sections.map((section) => ({
      ...section,
      quizzes: compact(
        section.quizzes.map((id) => quizzes.find((q) => q.id === id))
      ),
    }));

    // Only the main quiz
    const hasSectionQuizzes =
      sections.filter((s) => s.quizzes.length > 0).length > 0;

    // Redirect into the mainQuiz if it's the only one
    if (mainQuiz && !hasSectionQuizzes) {
      return {
        redirect: {
          destination: `/quiz/${mainQuiz.slug}`,
          permanent: false,
        },
      };
    }

    return {
      props: { parent: category.title, title, text, mainQuiz, sections },
    };
  })
);

export default SubCategory;
